/* eslint-disable max-lines-per-function */
import React from "react";
import { Link, RouteComponentProps } from "@reach/router";
import { gql } from "graphql-tag";
import { useQuery } from "@apollo/client";
import moment from "moment";
import MaterialTable, { Column, MTableToolbar } from "@material-table/core";
import { Grid } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { letterJobQueries, letterJobQueries_letterJobs as letterJobs, RelatedEntityType } from "types/gql-types";

const LETTERS_QUERY = gql`
query letterJobQueries($id: ID!, $type: RelatedEntityType!, $startDate: Date!, $endDate: Date!) {
    letterJobs(id: $id, type: $type, startDate: $startDate, endDate: $endDate) {
      id
      insertedAt
      pageCount
      lobLetterId
      interest {
        id
        userId
        householdMember0FirstName
        householdMember0LastName
        organization {
          id
          name
        }
        interestList {
          property {
            id
            name
          }
        }
      }
    }
}
`;

const renderUserColumn = (
  {
    interest: {
      userId,
      householdMember0FirstName: firstName,
      householdMember0LastName: lastName,
    },
  }:letterJobs,
) => {
  const fullName = `${firstName} ${lastName}`;

  if (!userId) {
    return <span>{fullName}</span>;
  }

  return <Link to={`/dash/admin/users/${userId}`}>{fullName}</Link>;
};

const renderLetterDownload = ({ lobLetterId }:letterJobs) =>
  <a target="_blank" rel="noreferrer" href={`/downloads/letter/${lobLetterId}`}>View Letter</a>;

const columns: Array<Column<letterJobs>> = [
  { title: "Date", field: "insertedAt" },
  { title: "Organization", field: "interest.organization.name" },
  { title: "Property", field: "interest.interestList.property.name" },
  { title: "User", field: "interest.householdMember0FirstName", render: renderUserColumn },
  { title: "Page Count", field: "pageCount" },
  { title: "Link", field: "lobLetterId", render: renderLetterDownload },
];

interface LetterSearchProps extends RouteComponentProps
{
  type?: "p" | "o";
  id?: string;
  month?: string;
}

const LettersSearchPage:React.FC<LetterSearchProps> = ({ type, id, month }) => {
  const [startDate, setStartDate] = React.useState(moment(month, "MM-YYYY").startOf("month"));
  const [endDate, setEndDate] = React.useState(moment(startDate).endOf("month"));

  const typedType = type === "p" ? RelatedEntityType.PROPERTY : RelatedEntityType.ORGANIZATION;

  const { data, error, loading } =
    useQuery<letterJobQueries>(
      LETTERS_QUERY,
      { variables: {
        id,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
        type: typedType,
      } },
    );

  if (error) return <span>Server Error</span>;

  const tableData:letterJobs[] =
    data && data.letterJobs ? data.letterJobs : [];

  return (
    <MaterialTable
      title="Letters"
      data={tableData}
      columns={columns}
      isLoading={loading}
      options={{ pageSize: 50 }}

      components={{ Toolbar: function Toolbar (props) {
        return (
          <div>
            <MTableToolbar {...props} />
            <Grid container spacing={2} style={{ paddingLeft: 16 }}>
              <Grid item>
                <KeyboardDatePicker
                  label="Start Date"
                  value={startDate}
                  format="MM/dd/yyyy"
                  autoOk
                  onChange={(date) => {
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      setStartDate(moment(date));
                    }
                  }} />
              </Grid>
              <Grid item>
                <KeyboardDatePicker
                  label="End Date"
                  value={endDate}
                  format="MM/dd/yyyy"
                  autoOk
                  onChange={(date) => {
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      setEndDate(moment(date));
                    }
                  }} />
              </Grid>
            </Grid>
          </div>
        );
      } }}
    />
  );
};

export default LettersSearchPage;
